<template lang="pug">
div
  b-card
    b-row.mb-2
      b-col(md="6")
        h4.mb-0
          feather-icon(icon="GridIcon" size="20" class="mr-50")
          | Mapa de Riesgos — Probabilidad × Impacto

      b-col(md="6" class="text-right")
        b-button-group.mr-1
          b-button(
            :variant="riskType === 'inherent' ? 'primary' : 'outline-primary'"
            size="sm"
            @click="riskType = 'inherent'"
          ) Inherente
          b-button(
            :variant="riskType === 'residual' ? 'primary' : 'outline-primary'"
            size="sm"
            @click="riskType = 'residual'"
          ) Residual

        b-button.mr-1(
          v-if="hasPermission('privacy-risk-map', 'export')"
          variant="outline-success"
          size="sm"
          @click="handleExportExcel"
          :disabled="isLoading || isExportingExcel"
        )
          b-spinner.mr-50(v-if="isExportingExcel" small)
          feather-icon(v-else icon="DownloadIcon" class="mr-50")
          | Excel

        b-button(
          v-if="hasPermission('privacy-risk-map', 'export')"
          variant="outline-info"
          size="sm"
          @click="handleExportPNG"
          :disabled="isLoading || isExportingPNG"
        )
          b-spinner.mr-50(v-if="isExportingPNG" small)
          feather-icon(v-else icon="ImageIcon" class="mr-50")
          | PNG

    b-row.mb-1
      b-col
        small.text-muted Dos vistas del mismo mapa: a la izquierda los valores de riesgo {{ riskType === 'inherent' ? 'inherente' : 'residual' }} (referencia fija PxI). A la derecha, cuántos tratamientos del {{ riskType === 'inherent' ? 'ARI' : 'ARR' }} caen en cada cuadrante (dinámico).

    div(v-if="isLoading")
      b-row
        b-col.text-center.py-5
          b-spinner(variant="primary")
          p.mt-2 Cargando mapa de riesgos...

    div(v-else ref="matrixContainer")
      b-row
        //- Matriz de Valores Fijos
        b-col(md="6")
          table.risk-matrix
            thead
              tr
                th.corner-cell(colspan="2" rowspan="2")
                  | Valoración del Riesgo
                  br
                  | (Probabilidad × Impacto)
                th.impact-header(colspan="5") IMPACTO
              tr
                th.impact-label(v-for="i in 5" :key="'ih-'+i")
                  div.impact-number {{ i }}
                  div.impact-text {{ impactLabels[i-1] }}
            tbody
              tr(v-for="p in 5" :key="'vr-'+p")
                td.prob-axis-cell(v-if="p === 1" :rowspan="5")
                  span.prob-axis-text PROBABILIDAD
                td.prob-label {{ p }}
                td.value-cell(
                  v-for="i in 5"
                  :key="'vc-'+p+'-'+i"
                  :style="{ backgroundColor: getCellColor(p * i) }"
                )
                  span.cell-value {{ p * i }}

        //- Matriz de Contadores
        b-col(md="6")
          table.risk-matrix
            thead
              tr
                th.corner-cell(colspan="2" rowspan="2")
                  | Tratamientos por cuadrante
                  br
                  | (contador dinámico)
                th.impact-header(colspan="5") IMPACTO
              tr
                th.impact-label(v-for="i in 5" :key="'ih2-'+i")
                  div.impact-number {{ i }}
                  div.impact-text {{ impactLabels[i-1] }}
            tbody
              tr(v-for="p in 5" :key="'cr-'+p")
                td.prob-axis-cell(v-if="p === 1" :rowspan="5")
                  span.prob-axis-text PROBABILIDAD
                td.prob-label {{ p }}
                td.counter-cell(
                  v-for="i in 5"
                  :key="'cc-'+p+'-'+i"
                  :style="{ backgroundColor: getCellColor(p * i) }"
                  :class="{ 'clickable': getCellCount(p, i) > 0 }"
                  @click="selectCell(p, i)"
                )
                  span.cell-counter(:class="{ 'has-items': getCellCount(p, i) > 0, 'no-items': getCellCount(p, i) === 0 }")
                    | {{ getCellCount(p, i) }}

      //- Tabla de Resumen por Nivel
      b-row.mt-3
        b-col(cols="12")
          h5.text-center.mb-2 TRATAMIENTOS POR NIVEL DE RIESGO
          b-table-simple(bordered small responsive)
            b-thead
              b-tr
                b-th NIVEL
                b-th RANGO PxI
                b-th CANTIDAD
                b-th % DEL TOTAL
                b-th RIESGO PROMEDIO
            b-tbody
              b-tr(v-for="row in summaryTable" :key="row.level")
                b-td
                  b-badge(:style="{ backgroundColor: row.color, color: '#fff' }") {{ row.level }}
                b-td {{ getLevelRange(row.level) }}
                b-td {{ row.count }}
                b-td {{ row.percentage }}%
                b-td {{ row.avgRisk || '-' }}

    //- Modal Drill-down
    b-modal(
      v-model="showDrillDown"
      :title="drillDownTitle"
      size="lg"
      hide-footer
      scrollable
    )
      b-table(
        :items="treatmentsInCell"
        :fields="drillDownFields"
        striped
        hover
        responsive
        show-empty
        empty-text="Sin tratamientos en esta celda"
      )
        template(#cell(treatmentId)="data")
          strong {{ data.item.treatmentId }}

        template(#cell(level)="data")
          b-badge(:variant="getLevelVariant(data.item.level)") {{ translateLevel(data.item.level) }}

        template(#cell(eipdFinal)="data")
          b-badge(:variant="data.item.eipdFinal ? 'light-danger' : 'light-secondary'")
            | {{ data.item.eipdFinal ? 'Sí' : 'No' }}

        template(#cell(actions)="data")
          b-button(
            v-if="canEditItem"
            variant="flat-primary"
            size="sm"
            @click="goToAnalysis(data.item)"
          )
            feather-icon(icon="ExternalLinkIcon")
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { BButtonGroup, BBadge, BTableSimple, BThead, BTbody, BTr, BTh, BTd, BTable, BModal, BSpinner } from 'bootstrap-vue'
import { useRouter } from '@core/utils/utils'
import { hasPermission } from '@/composables/usePermission'
import useRiskMap from './useRiskMap'
import useExportRiskMap from './useExportRiskMap'

export default {
  name: 'RiskMapList',
  components: {
    BButtonGroup,
    BBadge,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BTable,
    BModal,
    BSpinner,
  },
  setup() {
    const { router } = useRouter()
    const {
      riskType,
      isLoading,
      matrixData,
      treatmentsInCell,
      selectedCell,
      summaryTable,
      totalTreatments,
      LEVELS,
      getCellColor,
      getRiskLevel,
      fetchRiskData,
      selectCell: selectCellFn,
      clearSelection,
    } = useRiskMap()

    const { exportToExcel } = useExportRiskMap()

    const showDrillDown = ref(false)
    const isExportingExcel = ref(false)
    const isExportingPNG = ref(false)
    const canEditItem = computed(() => hasPermission('privacy-risk-map', 'edit'))

    const impactLabels = ['Nulo/Muy Bajo', 'Bajo', 'Medio', 'Alto', 'Muy Alto']

    const drillDownFields = [
      { key: 'treatmentId', label: 'ID', sortable: true },
      { key: 'nombre', label: 'Nombre', sortable: true },
      { key: 'level', label: 'Nivel', sortable: true },
      { key: 'eipdFinal', label: 'EIPD Requerida', sortable: true },
      { key: 'actions', label: '' },
    ]

    const drillDownTitle = computed(() => {
      if (!selectedCell.value) return ''
      const { p, i, risk } = selectedCell.value
      const level = getRiskLevel(risk)
      return `P=${p}, I=${i} — Riesgo ${risk} (${level.key}) — ${treatmentsInCell.value.length} tratamiento(s)`
    })

    const getCellCount = (p, i) => {
      const key = `${p}-${i}`
      return matrixData.value[key]?.length || 0
    }

    const selectCell = (p, i) => {
      selectCellFn(p, i)
      if (treatmentsInCell.value.length > 0) {
        showDrillDown.value = true
      }
    }

    const getLevelRange = (levelKey) => {
      const level = LEVELS.find(l => l.key === levelKey)
      if (!level) return ''
      if (level.min === level.max) return `${level.min}`
      return `${level.min}–${level.max}`
    }

    const getLevelVariant = (level) => {
      const map = {
        'Crítico': 'danger',
        'Muy Alto': 'danger',
        'Alto': 'warning',
        'Medio': 'warning',
        'Bajo': 'success',
        critical: 'danger',
        veryHigh: 'danger',
        high: 'warning',
        medium: 'warning',
        low: 'success',
      }
      return map[level] || 'secondary'
    }

    const translateLevel = (level) => {
      const map = {
        critical: 'Crítico',
        veryHigh: 'Muy Alto',
        high: 'Alto',
        medium: 'Medio',
        low: 'Bajo',
      }
      return map[level] || level || '-'
    }

    const goToAnalysis = (item) => {
      const routeName = riskType.value === 'inherent' ? 'privacy-inherent-risk-edit' : 'privacy-residual-risk-edit'
      const resolved = router.resolve({ name: routeName, params: { id: item._id } })
      window.open(resolved.href, '_blank')
    }

    const handleExportExcel = async () => {
      if (!hasPermission('privacy-risk-map', 'export')) return
      isExportingExcel.value = true
      try {
        await new Promise(resolve => setTimeout(resolve, 50))
        exportToExcel(matrixData.value, summaryTable.value, riskType.value)
      } finally {
        isExportingExcel.value = false
      }
    }

    const matrixContainer = ref(null)

    const handleExportPNG = async () => {
      if (!hasPermission('privacy-risk-map', 'export')) return
      const container = matrixContainer.value
      if (!container) return

      isExportingPNG.value = true
      try {
        const html2canvas = (await import('html2canvas')).default
        const canvas = await html2canvas(container, {
          backgroundColor: '#ffffff',
          scale: 2,
        })
        canvas.toBlob(blob => {
          if (blob) {
            const link = document.createElement('a')
            link.download = `Mapa_Riesgos_${riskType.value === 'inherent' ? 'Inherente' : 'Residual'}_${new Date().toISOString().slice(0, 10)}.png`
            link.href = URL.createObjectURL(blob)
            link.click()
            URL.revokeObjectURL(link.href)
          }
          isExportingPNG.value = false
        })
      } catch (error) {
        console.log('html2canvas no disponible, exportando solo Excel')
        isExportingPNG.value = false
      }
    }

    onMounted(() => {
      fetchRiskData()
    })

    return {
      riskType,
      isLoading,
      isExportingExcel,
      isExportingPNG,
      canEditItem,
      hasPermission,
      matrixData,
      treatmentsInCell,
      selectedCell,
      summaryTable,
      totalTreatments,
      showDrillDown,
      impactLabels,
      drillDownFields,
      drillDownTitle,
      matrixContainer,
      getCellColor,
      getCellCount,
      selectCell,
      getLevelRange,
      getLevelVariant,
      translateLevel,
      goToAnalysis,
      handleExportExcel,
      handleExportPNG,
    }
  },
}
</script>

<style lang="scss" scoped>
.risk-matrix {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th, td {
    border: 1px solid #dee2e6;
    text-align: center;
    vertical-align: middle;
    padding: 8px 4px;
  }

  .corner-cell {
    background-color: #333;
    color: #fff;
    font-size: 12px;
    font-weight: bold;
    padding: 10px 8px;
  }

  .impact-header {
    background-color: #4a4a4a;
    color: #fff;
    font-weight: bold;
    font-size: 13px;
  }

  .prob-header-cell {
    background-color: #333;
    padding: 0;
  }

  .impact-label {
    background-color: #f8f9fa;
    font-size: 11px;
    padding: 4px 2px;

    .impact-number {
      font-weight: bold;
      font-size: 13px;
    }
    .impact-text {
      font-size: 10px;
      color: #666;
    }
  }

  .prob-axis-cell {
    background-color: #4a4a4a;
    width: 30px;
    min-width: 30px;
    padding: 0;
    vertical-align: middle;

    .prob-axis-text {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
      font-weight: bold;
      font-size: 12px;
      color: #fff;
      letter-spacing: 1px;
    }
  }

  .prob-label {
    background-color: #f8f9fa;
    font-weight: bold;
    font-size: 14px;
    width: 35px;
    min-width: 35px;
  }

  .value-cell {
    .cell-value {
      font-weight: bold;
      font-size: 16px;
      color: #fff;
      text-shadow: 0 1px 2px rgba(0,0,0,0.3);
    }
  }

  .counter-cell {
    .cell-counter {
      font-size: 16px;

      &.has-items {
        font-weight: bold;
        color: #fff;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
      }

      &.no-items {
        color: rgba(255,255,255,0.5);
        font-weight: normal;
      }
    }

    &.clickable {
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.85;
        box-shadow: inset 0 0 0 2px rgba(0,0,0,0.3);
      }
    }
  }
}
</style>
